import type { Task } from '@/shared/types/domain'

/**
 * Los buckets de `/due-soon` **los arma el servidor**.
 *
 * Acá no queda ni una línea que decida en qué tramo cae una fecha, y eso es el
 * arreglo: antes el frontend pedía `GET tasks/due-soon` —que devuelve los
 * cuatro buckets ya repartidos—, tiraba la asignación y volvía a bucketizar con
 * su propia regla. Las dos reglas no eran la misma: el backend corta "Esta
 * semana" en el **domingo de la semana en curso**, el frontend cortaba a **7
 * días rodantes**. Un viernes, una tarea del miércoles siguiente salía en
 * "Próximas 2 semanas" en el mail y en "Esta semana" en la pantalla.
 *
 * Ahora la clave, el orden y el reparto vienen del servidor, que es el mismo
 * que arma el digest por mail y los avisos de Slack. Una sola regla, imposible
 * de discrepar. El NOMBRE de cada tramo sí se escribe acá, en el idioma de
 * quien mira: es la única parte que el servidor no puede saber (`BUCKET_LABEL_KEY`).
 *
 * Lo que queda de este archivo es el agrupado DENTRO de un bucket (por persona
 * o por proyecto), que sí es una decisión de pantalla: el servidor no la conoce
 * ni tiene por qué.
 */

/** Las claves que manda el backend (`app/modules/tasks/service.py`, `BUCKETS`). */
export type BucketKey = 'overdue' | 'today' | 'this_week' | 'next_two_weeks'

/** Un bucket tal como lo manda el servidor, con las tareas ya adaptadas. */
export interface Bucket {
  key: string
  /** La etiqueta del servidor. Se dibuja sólo si la clave no está en `BUCKET_LABEL_KEY`. */
  label: string
  tasks: Task[]
}

/**
 * La CLAVE de catálogo de cada tramo, no su texto.
 *
 * El reparto lo sigue decidiendo el servidor —qué tarea cae en qué tramo, y en
 * qué orden vienen— pero el NOMBRE de cada tramo se escribe en el idioma de
 * quien mira, y el servidor no lo sabe: la misma respuesta la leen un cliente
 * en español, uno en portugués y uno en inglés.
 *
 * Son las de `due.bucket.*` y NO las de `due.status.*`, que es lo que nombra el
 * estado de UNA tarea. La diferencia no es cosmética: en español un tramo que
 * junta tareas se llama "Vencidas" y una tarea sola está "Vencida". Colapsar
 * las dos en una clave obliga a que un encabezado de grupo hable en singular —
 * y ahí el ahorro deja de ser ahorro.
 *
 * Una clave que no esté acá —un tramo nuevo que el backend estrene antes que la
 * pantalla— cae en la etiqueta que mandó el servidor: media pantalla en otro
 * idioma se puede leer, un encabezado vacío no.
 */
export const BUCKET_LABEL_KEY: Record<BucketKey, string> = {
  overdue: 'due.bucket.overdue',
  today: 'due.bucket.today',
  this_week: 'due.bucket.soon',
  next_two_weeks: 'due.bucket.upcoming',
}

/* ------------------------------------------------------------ agrupacion -- */

export type GroupBy = 'persona' | 'proyecto'

export interface TaskGroup {
  id: string
  /**
   * Lo que escribió alguien: el nombre de la persona, o `Empresa · Proyecto`.
   * No se traduce nunca. Vacío cuando el grupo no tiene nombre propio.
   */
  label: string
  /**
   * La clave de catálogo, cuando el nombre del grupo no es de nadie
   * ("Sin responsable"). Este módulo agrupa; no sabe en qué idioma se dibuja.
   */
  labelKey?: string
  /** Hue de la empresa, para la espina. Solo cuando se agrupa por proyecto. */
  hue: number | null
  tasks: Task[]
}

const UNASSIGNED_GROUP = '__sin-responsable__'

/** Agrupa DENTRO de un bucket, por persona o por proyecto. */
export function groupTasks(tasks: readonly Task[], groupBy: GroupBy): TaskGroup[] {
  const groups = new Map<string, TaskGroup>()

  for (const task of tasks) {
    const id = groupBy === 'persona' ? (task.assignee?.id ?? UNASSIGNED_GROUP) : task.project.id
    const label =
      groupBy === 'persona'
        ? (task.assignee?.display_name ?? '')
        : `${task.project.company.name} · ${task.project.name}`

    const group = groups.get(id) ?? {
      id,
      label,
      ...(id === UNASSIGNED_GROUP ? { labelKey: 'common.label.noOwner' } : {}),
      hue: groupBy === 'proyecto' ? task.project.company.color_hue : null,
      tasks: [],
    }
    group.tasks.push(task)
    groups.set(id, group)
  }

  return [...groups.values()].sort((a, b) => {
    // "Sin responsable" va ultimo: es un problema, no una persona.
    if (a.id === UNASSIGNED_GROUP) return 1
    if (b.id === UNASSIGNED_GROUP) return -1
    return b.tasks.length - a.tasks.length || a.label.localeCompare(b.label)
  })
}
